import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../api'
import { Plus, Cable, Copy, Check, Eye, EyeOff, Save, Zap, KeyRound, ShieldCheck, AlertTriangle, ExternalLink } from 'lucide-react'
import { Card, PageHeader, Button, Input, Field, Modal, ModalHeader, Alert, Badge, Table, THead, td, EmptyState } from '../components/ui'

const AUTH_GAGAL_STATUS_TONE = {
  BERHENTI: 'neutral',
  DIHAPUS: 'neutral',
}

const STATUS_LABEL = {
  online: { label: 'Online', tone: 'success' },
  offline: { label: 'Offline', tone: 'warning' },
  belum_pernah: { label: 'Belum Pernah Terhubung', tone: 'neutral' },
}

export default function PengaturanMikrotik() {
  const [data, setData] = useState([])
  const [modal, setModal] = useState(false)
  const [nama, setNama] = useState('')
  const [error, setError] = useState('')
  const [scriptModal, setScriptModal] = useState(null) // { nama, script }
  const [copied, setCopied] = useState(false)

  const [radiusAddress, setRadiusAddress] = useState('')
  const [radiusSecret, setRadiusSecret] = useState('')
  const [radiusShowSecret, setRadiusShowSecret] = useState(false)
  const [radiusSaving, setRadiusSaving] = useState(false)
  const [radiusSaved, setRadiusSaved] = useState(false)
  const [radiusError, setRadiusError] = useState('')

  // Kredensial per-router (2026-07-25, disatukan dari card "Mikrotik Produksi" yg dulu terpisah).
  // kredModal berisi baris yg lagi diedit -- host/user/password/port kosong sampai GET selesai.
  const [kredModal, setKredModal] = useState(null) // { id, nama, host, user, password, port, loading }
  const [kredShowPassword, setKredShowPassword] = useState(false)
  const [kredSaving, setKredSaving] = useState(false)
  const [kredError, setKredError] = useState('')

  const [aktifLoadingId, setAktifLoadingId] = useState(null)
  const [testing, setTesting] = useState({}) // { [id]: boolean }
  const [testResult, setTestResult] = useState({}) // { [id]: { ok, message } }

  // Laporan Auth Gagal (2026-08-29) -- lihat komentar GET /auth-gagal di server/routes/
  // mikrotikRouter.js. null = belum sempat dimuat sama sekali (jangan tampilkan EmptyState keliru),
  // [] = sudah dimuat & memang kosong.
  const [authGagal, setAuthGagal] = useState(null)
  const [authGagalError, setAuthGagalError] = useState('')

  useEffect(() => {
    load()
    loadRadiusPengaturan()
    loadAuthGagal()
    // Polling ringan supaya status Online/Offline ikut update begitu router check-in, tanpa
    // perlu refresh manual halaman.
    const t = setInterval(load, 15000)
    return () => clearInterval(t)
  }, [])

  async function loadAuthGagal() {
    setAuthGagalError('')
    try {
      const res = await api.get('/mikrotik-router/auth-gagal')
      setAuthGagal(res.data)
    } catch (e) {
      setAuthGagalError(e.response?.data?.message || 'Gagal memuat laporan (router aktif belum terhubung?)')
    }
  }

  async function load() {
    const res = await api.get('/mikrotik-router')
    setData(res.data)
  }

  async function loadRadiusPengaturan() {
    const res = await api.get('/mikrotik-router/pengaturan')
    setRadiusAddress(res.data.address || '')
    setRadiusSecret(res.data.secret || '')
  }

  async function simpanRadiusPengaturan() {
    setRadiusError(''); setRadiusSaving(true); setRadiusSaved(false)
    try {
      const res = await api.post('/mikrotik-router/pengaturan', { address: radiusAddress, secret: radiusSecret })
      setRadiusAddress(res.data.address || '')
      setRadiusSecret(res.data.secret || '')
      setRadiusSaved(true)
      setTimeout(() => setRadiusSaved(false), 2000)
    } catch (e) {
      setRadiusError(e.response?.data?.message || 'Gagal menyimpan pengaturan RADIUS')
    } finally {
      setRadiusSaving(false)
    }
  }

  async function bukaKredensial(row) {
    setKredError('')
    setKredShowPassword(false)
    setKredModal({ id: row.id, nama: row.nama, host: '', user: '', password: '', port: '', loading: true })
    const res = await api.get(`/mikrotik-router/${row.id}/kredensial`)
    setKredModal(k => (k && k.id === row.id)
      ? { ...k, host: res.data.host || '', user: res.data.user || '', password: res.data.password || '', port: res.data.port || '', loading: false }
      : k)
  }

  async function simpanKredensial() {
    setKredError(''); setKredSaving(true)
    try {
      await api.post(`/mikrotik-router/${kredModal.id}/kredensial`, {
        host: kredModal.host, user: kredModal.user, password: kredModal.password, port: kredModal.port
      })
      setKredModal(null)
      load()
    } catch (e) {
      setKredError(e.response?.data?.message || 'Gagal menyimpan kredensial')
    } finally {
      setKredSaving(false)
    }
  }

  async function jadikanAktif(row) {
    setAktifLoadingId(row.id)
    try {
      await api.post(`/mikrotik-router/${row.id}/aktifkan`)
      load()
    } finally {
      setAktifLoadingId(null)
    }
  }

  async function tesKoneksi(row) {
    setTesting(t => ({ ...t, [row.id]: true }))
    setTestResult(t => ({ ...t, [row.id]: null }))
    try {
      const res = await api.post(`/mikrotik-router/${row.id}/test`)
      setTestResult(t => ({ ...t, [row.id]: { ok: true, message: res.data.message } }))
    } catch (e) {
      setTestResult(t => ({ ...t, [row.id]: { ok: false, message: e.response?.data?.message || 'Gagal terhubung' } }))
    } finally {
      setTesting(t => ({ ...t, [row.id]: false }))
    }
  }

  function bukaTambah() {
    setNama(''); setError(''); setModal(true)
  }

  async function simpan() {
    if (!nama.trim()) { setError('Nama router wajib diisi'); return }
    try {
      const res = await api.post('/mikrotik-router', { nama })
      setModal(false)
      load()
      setCopied(false)
      setScriptModal({ nama: res.data.nama, script: res.data.script })
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal menyimpan')
    }
  }

  async function lihatScript(row) {
    const res = await api.get(`/mikrotik-router/${row.id}/script`)
    setCopied(false)
    setScriptModal({ nama: row.nama, script: res.data.script })
  }

  async function hapus(row) {
    if (!window.confirm(`Hapus router "${row.nama}"? Script yang sudah ditempel di router tetap akan mengirim heartbeat, tapi akan ditolak (token tidak dikenal).`)) return
    await api.delete(`/mikrotik-router/${row.id}`)
    load()
  }

  function salinScript() {
    navigator.clipboard.writeText(scriptModal.script)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div>
      <PageHeader
        title="Koneksi Mikrotik"
        subtitle='Daftar Mikrotik yang terhubung dengan aplikasi ini — tandai salah satu sebagai "Aktif" untuk dipakai fitur live (status Perangkat Pelanggan, Diagnostik, redaman, AI Asisten). Router lain cukup lapor status lewat heartbeat, belum tentu dikendalikan aplikasi.'
        actions={<Button icon={Plus} onClick={bukaTambah}>Tambah Mikrotik</Button>}
      />

      <Card style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 14.5, marginBottom: 4 }}>Pengaturan RADIUS</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 16 }}>
          Diisi sekali di sini, otomatis terpakai di script "Lihat Script" tiap router (baris opsional <code>/radius add</code>). Tidak perlu edit file server atau restart apa pun — perubahan secret otomatis diteruskan ke server RADIUS dalam beberapa detik.
        </div>
        {radiusError && <Alert type="error">{radiusError}</Alert>}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Field label="Alamat Server RADIUS" style={{ flex: '1 1 220px' }}>
            <Input value={radiusAddress} onChange={e => setRadiusAddress(e.target.value)} placeholder="Mis. 10.10.10.254 (IP LAN server ini)" />
          </Field>
          <Field label="Secret RADIUS" style={{ flex: '1 1 220px' }}>
            <div style={{ position: 'relative' }}>
              <Input
                type={radiusShowSecret ? 'text' : 'password'}
                value={radiusSecret}
                onChange={e => setRadiusSecret(e.target.value)}
                placeholder="Secret dibagi rata semua router"
                style={{ paddingRight: 38 }}
              />
              <button
                type="button"
                onClick={() => setRadiusShowSecret(s => !s)}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 4 }}
                aria-label={radiusShowSecret ? 'Sembunyikan secret' : 'Tampilkan secret'}
              >
                {radiusShowSecret ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </Field>
          <Button icon={radiusSaved ? Check : Save} onClick={simpanRadiusPengaturan} loading={radiusSaving} style={{ flex: '0 0 auto' }}>
            {radiusSaved ? 'Tersimpan' : 'Simpan'}
          </Button>
        </div>
      </Card>

      {data.length === 0 ? (
        <Card><EmptyState icon={Cable} title="Belum ada router terdaftar" hint='Klik "Tambah Mikrotik" untuk mulai' /></Card>
      ) : (
        <Table>
          <THead cols={['Nama', 'Aktif', 'Status', 'Identity', 'Versi RouterOS', 'Check-in Terakhir', '']} />
          <tbody>
            {data.map(r => {
              const s = STATUS_LABEL[r.status] || STATUS_LABEL.belum_pernah
              const tr = testResult[r.id]
              return (
                <tr key={r.id}>
                  <td style={{ ...td, fontWeight: 600 }}>{r.nama}</td>
                  <td style={td}>
                    {r.aktif ? <Badge tone="success"><ShieldCheck size={12} /> Aktif</Badge> : <Badge tone="neutral">-</Badge>}
                  </td>
                  <td style={td}><Badge tone={s.tone}>{s.label}</Badge></td>
                  <td style={td}>{r.identity || '-'}</td>
                  <td style={td}>{r.versi || '-'}</td>
                  <td style={td}>{r.last_checkin_at || '-'}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {!r.aktif && (
                        <Button size="sm" variant="soft" icon={ShieldCheck} loading={aktifLoadingId === r.id} onClick={() => jadikanAktif(r)}>Jadikan Aktif</Button>
                      )}
                      <Button size="sm" variant="soft" icon={KeyRound} onClick={() => bukaKredensial(r)}>Kredensial</Button>
                      <Button size="sm" variant="soft" icon={Zap} loading={!!testing[r.id]} disabled={!r.punya_kredensial} onClick={() => tesKoneksi(r)}>Tes</Button>
                      <Button size="sm" variant="soft" onClick={() => lihatScript(r)}>Script</Button>
                      <Button size="sm" variant="danger-soft" onClick={() => hapus(r)}>Hapus</Button>
                    </div>
                    {tr && (
                      <div style={{ fontSize: 11.5, marginTop: 4, color: tr.ok ? 'var(--success)' : 'var(--danger)' }}>{tr.message}</div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}

      <Card style={{ padding: 20, marginTop: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 14.5, marginBottom: 4 }}>Pelanggan Gagal Auth Berulang</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 16 }}>
          Diambil dari buffer log router Aktif (~beberapa jam terakhir) — username yang alat/ONT-nya masih tertancap & terus mencoba konek PPPoE walau ditolak RADIUS. Kalau statusnya BERHENTI/sudah dihapus, itu memang wajar (perlu teknisi menarik alatnya di lapangan supaya betul-betul berhenti mencoba). Kalau statusnya AKTIF/ISOLIR, itu anomali — kemungkinan password di alat sudah tidak sinkron dengan yang tersimpan di Daftar Pelanggan.
        </div>
        {authGagalError && <Alert type="error">{authGagalError}</Alert>}
        {authGagal === null ? null : authGagal.length === 0 ? (
          <EmptyState icon={ShieldCheck} title="Tidak ada" hint="Tidak ada percobaan auth gagal berulang di buffer log saat ini" />
        ) : (
          <Table>
            <THead cols={['Username', 'Nama', 'Status', 'Jumlah Gagal', 'Terakhir Dicoba', 'Sebab', '']} />
            <tbody>
              {authGagal.map(r => (
                <tr key={r.username}>
                  <td style={{ ...td, fontWeight: 600 }}>{r.username}</td>
                  <td style={td}>{r.nama || '-'}</td>
                  <td style={td}><Badge tone={AUTH_GAGAL_STATUS_TONE[r.status] || 'warning'}>{r.status}</Badge></td>
                  <td style={td}>{r.jumlah_gagal}</td>
                  <td style={td}>{r.terakhir_dicoba}</td>
                  <td style={{ ...td, fontSize: 12, color: 'var(--text-muted)', maxWidth: 260 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                      {r.status !== 'BERHENTI' && r.status !== 'DIHAPUS' && <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1, color: 'var(--warning)' }} />}
                      <span>{r.sebab}</span>
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {r.status !== 'DIHAPUS' && (
                      <Link to={`/pelanggan-kontak?search=${encodeURIComponent(r.username)}`}>
                        <Button size="sm" variant="soft" icon={ExternalLink}>Buka di Daftar Pelanggan</Button>
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {modal && (
        <Modal onClose={() => setModal(false)} width={420}>
          <ModalHeader title="Tambah Mikrotik" onClose={() => setModal(false)} />
          {error && <Alert type="error">{error}</Alert>}
          <Field label="Nama Router *" style={{ marginBottom: 22 }}>
            <Input value={nama} onChange={e => setNama(e.target.value)} placeholder="Mis. Mikrotik Test / Kantor Cabang A" />
          </Field>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="neutral" onClick={() => setModal(false)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button onClick={simpan} style={{ flex: 1, justifyContent: 'center' }}>Simpan & Buat Script</Button>
          </div>
        </Modal>
      )}

      {kredModal && (
        <Modal onClose={() => setKredModal(null)} width={480}>
          <ModalHeader
            title={`Kredensial Mikrotik — ${kredModal.nama}`}
            subtitle='Kredensial API RouterOS router ini. Kalau router ini ditandai "Aktif", inilah yang benar-benar dipakai live oleh seluruh aplikasi.'
            onClose={() => setKredModal(null)}
          />
          {kredError && <Alert type="error">{kredError}</Alert>}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 22 }}>
            <Field label="Host / IP" style={{ flex: '1 1 200px' }}>
              <Input value={kredModal.host} onChange={e => setKredModal(k => ({ ...k, host: e.target.value }))} placeholder="mis. 10.10.10.1" disabled={kredModal.loading} />
            </Field>
            <Field label="Port API" style={{ flex: '0 1 110px' }}>
              <Input value={kredModal.port} onChange={e => setKredModal(k => ({ ...k, port: e.target.value }))} placeholder="8728" disabled={kredModal.loading} />
            </Field>
            <Field label="Username" style={{ flex: '1 1 160px' }}>
              <Input value={kredModal.user} onChange={e => setKredModal(k => ({ ...k, user: e.target.value }))} placeholder="Username API Mikrotik" disabled={kredModal.loading} />
            </Field>
            <Field label="Password" style={{ flex: '1 1 200px' }}>
              <div style={{ position: 'relative' }}>
                <Input
                  type={kredShowPassword ? 'text' : 'password'}
                  value={kredModal.password}
                  onChange={e => setKredModal(k => ({ ...k, password: e.target.value }))}
                  placeholder="Password API Mikrotik"
                  style={{ paddingRight: 38 }}
                  disabled={kredModal.loading}
                />
                <button
                  type="button"
                  onClick={() => setKredShowPassword(s => !s)}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 4 }}
                  aria-label={kredShowPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                >
                  {kredShowPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="neutral" onClick={() => setKredModal(null)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button icon={Save} onClick={simpanKredensial} loading={kredSaving} disabled={kredModal.loading} style={{ flex: 1, justifyContent: 'center' }}>Simpan</Button>
          </div>
        </Modal>
      )}

      {scriptModal && (
        <Modal onClose={() => setScriptModal(null)} width={640}>
          <ModalHeader
            title={`Script untuk "${scriptModal.nama}"`}
            subtitle="Tempel di Winbox > New Terminal pada router Mikrotik, lalu Enter"
            onClose={() => setScriptModal(null)}
          />
          <Alert type="success">
            Script ini aman ditempel ulang kapan saja (otomatis mengganti script/scheduler lama). Router akan mengirim status tiap 1 menit ke aplikasi ini.
          </Alert>
          {scriptModal.script.includes('use-radius=yes') && (
            <Alert type="error">
              Script ini JUGA mengaktifkan RADIUS untuk SEMUA PPPoE di router ini ("use-radius=yes" berlaku global, bukan per-pelanggan). Aman untuk router baru/test tanpa pelanggan aktif. Kalau router ini SUDAH punya pelanggan berjalan, pastikan dulu data username/password PPP di halaman Daftar Pelanggan sudah cocok sebelum menempel script ini — baca komentar lengkap di dalam script.
            </Alert>
          )}
          <textarea
            readOnly
            value={scriptModal.script}
            onClick={e => e.target.select()}
            style={{
              width: '100%', height: 220, fontFamily: 'monospace', fontSize: 12.5,
              background: '#101018', color: '#d4d4e0', border: 'none', borderRadius: 'var(--radius-sm)',
              padding: 14, resize: 'vertical', marginBottom: 14, boxSizing: 'border-box'
            }}
          />
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="neutral" onClick={() => setScriptModal(null)} style={{ flex: 1, justifyContent: 'center' }}>Tutup</Button>
            <Button icon={copied ? Check : Copy} onClick={salinScript} style={{ flex: 1, justifyContent: 'center' }}>
              {copied ? 'Tersalin' : 'Salin Script'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
